import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Home, Save, RefreshCw, ChevronUp, ChevronDown, Plus, Trash2,
  Edit2, Eye, EyeOff, Image, Link as LinkIcon, Type, Tag,
  ToggleLeft, ToggleRight, Menu, Layout, Star, Truck, Shield,
  CheckCircle, Headphones, Mail, X, GripVertical, Settings,
  ChevronRight, Package, Monitor, Grid, Users, HelpCircle,
  Film, Smartphone, Layers, Heart, Zap, ShoppingBag, PanelTop,
} from "lucide-react";
import ImageUploadInput from "../../components/ImageUploadInput";
import NewSectionsEditor, { NewSectionContentRenderer } from "./NewSectionsEditor";
import CustomSectionEditor, { DeviceSelect } from "./CustomSectionsEditor";
import HeaderAdmin from "./HeaderAdmin";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function uid() {
  return `id_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

// ── Shared small components ────────────────────────────────────────────────
function Field({ label, children, hint }) {
  return (
    <div className="space-y-1">
      <label className="block text-xs font-semibold text-slate-600">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-slate-400">{hint}</p>}
    </div>
  );
}

function Input({ value, onChange, placeholder, type = "text", testId }) {
  return (
    <input
      data-testid={testId}
      type={type}
      value={value || ""}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-500 bg-white text-slate-800"
    />
  );
}

function Toggle({ value, onChange, label }) {
  return (
    <button
      onClick={() => onChange(!value)}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all border ${
        value ? "bg-orange-500 text-white border-orange-500" : "bg-white text-slate-500 border-slate-300"
      }`}
    >
      {value ? <ToggleRight className="w-3.5 h-3.5" /> : <ToggleLeft className="w-3.5 h-3.5" />}
      {label || (value ? "Enabled" : "Disabled")}
    </button>
  );
}

function SectionCard({ title, icon: Icon, enabled, onToggle, onEdit, onMoveUp, onMoveDown, isFirst, isLast, isOpen, badge }) {
  return (
    <div className={`border rounded-xl overflow-hidden transition-all ${isOpen ? "border-orange-300 shadow-sm" : "border-slate-200"}`}>
      <div className={`flex items-center gap-3 px-4 py-3 ${isOpen ? "bg-orange-50" : "bg-white hover:bg-slate-50"}`}>
        <div className="w-7 h-7 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
          <Icon className="w-3.5 h-3.5 text-slate-500" />
        </div>
        <div className="flex-1 min-w-0 flex items-center gap-2">
          <p className="text-sm font-semibold text-slate-800">{title}</p>
          {badge && (
            <span className="bg-orange-100 text-orange-600 text-[9px] font-black px-1.5 py-0.5 rounded-full uppercase tracking-wide">{badge}</span>
          )}
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <div className="flex flex-col gap-0.5">
            <button onClick={onMoveUp} disabled={isFirst}
              className="w-5 h-5 flex items-center justify-center text-slate-400 hover:text-slate-700 disabled:opacity-20 disabled:cursor-not-allowed">
              <ChevronUp className="w-3 h-3" />
            </button>
            <button onClick={onMoveDown} disabled={isLast}
              className="w-5 h-5 flex items-center justify-center text-slate-400 hover:text-slate-700 disabled:opacity-20 disabled:cursor-not-allowed">
              <ChevronDown className="w-3 h-3" />
            </button>
          </div>
          <Toggle value={enabled} onChange={onToggle} />
          <button onClick={onEdit}
            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all border ${
              isOpen ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-600 border-slate-300 hover:border-slate-500"
            }`}>
            <Edit2 className="w-3 h-3" />
            {isOpen ? "Close" : "Edit"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Section Editors ────────────────────────────────────────────────────────

// Generic item list editor (add/remove/reorder)
function ItemListEditor({ items, onUpdate, renderItem, emptyItem, addLabel = "Add Item" }) {
  const moveItem = (idx, dir) => {
    const arr = [...items];
    const swapIdx = idx + dir;
    if (swapIdx < 0 || swapIdx >= arr.length) return;
    [arr[idx], arr[swapIdx]] = [arr[swapIdx], arr[idx]];
    onUpdate(arr);
  };
  const removeItem = (idx) => onUpdate(items.filter((_, i) => i !== idx));
  const addItem = () => onUpdate([...items, { ...emptyItem, id: uid() }]);
  const updateItem = (idx, patch) => {
    const arr = [...items];
    arr[idx] = { ...arr[idx], ...patch };
    onUpdate(arr);
  };

  return (
    <div className="space-y-3">
      {items.map((item, idx) => (
        <div key={item.id || idx} className="border border-slate-200 rounded-xl overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 border-b border-slate-200">
            <GripVertical className="w-3.5 h-3.5 text-slate-400" />
            <span className="text-xs font-semibold text-slate-600 flex-1">Item {idx + 1}</span>
            <button onClick={() => moveItem(idx, -1)} disabled={idx === 0}
              className="w-5 h-5 flex items-center justify-center text-slate-400 hover:text-slate-700 disabled:opacity-20">
              <ChevronUp className="w-3 h-3" />
            </button>
            <button onClick={() => moveItem(idx, 1)} disabled={idx === items.length - 1}
              className="w-5 h-5 flex items-center justify-center text-slate-400 hover:text-slate-700 disabled:opacity-20">
              <ChevronDown className="w-3 h-3" />
            </button>
            <button onClick={() => removeItem(idx)}
              className="w-5 h-5 flex items-center justify-center text-red-400 hover:text-red-600 transition-colors">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="p-4 bg-white">
            {renderItem(item, (patch) => updateItem(idx, patch))}
          </div>
        </div>
      ))}
      <button onClick={addItem}
        className="w-full flex items-center justify-center gap-1.5 py-2 border-2 border-dashed border-slate-300 hover:border-orange-400 rounded-xl text-xs font-semibold text-slate-500 hover:text-orange-600 transition-all">
        <Plus className="w-3.5 h-3.5" /> {addLabel}
      </button>
    </div>
  );
}

// Hero Editor
function HeroEditor({ data, onChange }) {
  return (
    <div className="space-y-4 p-4">
      <Field label="Autoplay Interval (ms)">
        <Input value={data.autoplay_interval || 5000} onChange={v => onChange({ ...data, autoplay_interval: parseInt(v) || 5000 })} type="number" placeholder="5000" />
      </Field>
      <div>
        <p className="text-xs font-semibold text-slate-600 mb-3">Slides ({(data.slides || []).length})</p>
        <ItemListEditor
          items={data.slides || []}
          onUpdate={slides => onChange({ ...data, slides })}
          addLabel="Add Slide"
          emptyItem={{ title: "", subtitle: "", badge: "", image_url: "", bg_color: "#1e293b", original_price: "", sale_price: "", cta_text: "Shop Now", cta_url: "/" }}
          renderItem={(slide, update) => (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Desktop Banner Image URL" hint="Recommended: 1440×500px">
                <ImageUploadInput value={slide.image_url} onChange={v => update({ image_url: v })} placeholder="https://…/banner-desktop.jpg" />
              </Field>
              <Field label="Mobile Banner Image URL" hint="Recommended: 768×400px">
                <ImageUploadInput value={slide.image_url_mobile || ""} onChange={v => update({ image_url_mobile: v })} placeholder="https://…/banner-mobile.jpg" />
              </Field>
              <Field label="Background Color (fallback)">
                <div className="flex gap-2">
                  <Input value={slide.bg_color} onChange={v => update({ bg_color: v })} placeholder="#1e293b" />
                  <input type="color" value={slide.bg_color || "#1e293b"} onChange={e => update({ bg_color: e.target.value })} className="w-10 h-9 rounded border border-slate-300 cursor-pointer p-0.5" />
                </div>
              </Field>
              <Field label="Badge Text">
                <Input value={slide.badge} onChange={v => update({ badge: v })} placeholder="Special Offer" />
              </Field>
              <Field label="Title">
                <Input value={slide.title} onChange={v => update({ title: v })} placeholder="4K UHD T.V" />
              </Field>
              <Field label="Subtitle">
                <Input value={slide.subtitle} onChange={v => update({ subtitle: v })} placeholder="Short description…" />
              </Field>
              <Field label="Original Price">
                <Input value={slide.original_price} onChange={v => update({ original_price: v })} placeholder="899" />
              </Field>
              <Field label="Sale Price">
                <Input value={slide.sale_price} onChange={v => update({ sale_price: v })} placeholder="699" />
              </Field>
              <Field label="CTA Button Text">
                <Input value={slide.cta_text} onChange={v => update({ cta_text: v })} placeholder="Shop Now" />
              </Field>
              <Field label="CTA Link URL">
                <Input value={slide.cta_url} onChange={v => update({ cta_url: v })} placeholder="/products/tv" />
              </Field>
            </div>
          )}
        />
      </div>
    </div>
  );
}

// Category Grid Editor (multiple product grid sections)
function CategoryGridEditor({ data, onChange, collections }) {
  const sections = data.items || [];
  const updateSections = (items) => onChange({ ...data, items });

  return (
    <div className="p-4">
      <p className="text-xs text-slate-500 mb-4">
        Add any number of product grid sections. Each section shows products from a chosen collection in a 5-column grid — exactly like the "Recommended For You" layout, with a <strong>"View More"</strong> link on the right.
      </p>
      <ItemListEditor
        items={sections}
        onUpdate={updateSections}
        addLabel="+ Add Product Grid"
        emptyItem={{ title: "", collection: "", limit: 10, enabled: true }}
        renderItem={(section, update) => {
          // Auto-populate collection from title when collection is empty
          const collectionValue = section.collection || "";
          return (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-bold text-slate-700">Grid Settings</p>
                <Toggle value={section.enabled !== false} onChange={v => update({ enabled: v })} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Section Title" hint="Shown as the section heading">
                  <Input
                    value={section.title || ""}
                    onChange={v => {
                      // Auto-fill collection if not already manually set
                      const patch = { title: v };
                      if (!section.collection) patch.collection = v;
                      update(patch);
                    }}
                    placeholder="Electronics"
                  />
                </Field>
                <Field label="Product Collection" hint="Products from this collection are shown">
                  <div className="flex gap-2">
                    <Input value={collectionValue} onChange={v => update({ collection: v })} placeholder="Electronics" />
                    {collections.length > 0 && (
                      <select
                        value={collectionValue}
                        onChange={e => update({ collection: e.target.value })}
                        className="text-xs border border-slate-300 rounded-lg px-2 py-2 focus:outline-none focus:border-orange-500 bg-white text-slate-700 max-w-[130px]"
                      >
                        <option value="">Pick…</option>
                        {collections.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    )}
                  </div>
                </Field>
                <Field label="Max Products to Show" hint="In multiples of 5">
                  <select
                    value={section.limit || 10}
                    onChange={e => update({ limit: parseInt(e.target.value) })}
                    className="w-full text-xs border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-500 bg-white text-slate-700"
                  >
                    {[5, 10, 15, 20, 25, 30, 40, 50].map(n => (
                      <option key={n} value={n}>{n} products</option>
                    ))}
                  </select>
                </Field>
              </div>
              {!collectionValue && section.title && (
                <p className="text-[11px] text-amber-600 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                  Collection not set — showing "{section.title}" products using title as collection name. Use the picker above to set it explicitly.
                </p>
              )}
            </div>
          );
        }}
      />
    </div>
  );
}

// Brands Editor
function BrandsEditor({ data, onChange }) {
  return (
    <div className="p-4 space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Section Title">
          <Input value={data.title} onChange={v => onChange({ ...data, title: v })} placeholder="Brand We Love" />
        </Field>
        <Field label="View All URL (leave blank to hide)">
          <Input value={data.view_all_url} onChange={v => onChange({ ...data, view_all_url: v })} placeholder="/brands" />
        </Field>
      </div>
      <ItemListEditor
        items={data.items || []}
        onUpdate={items => onChange({ ...data, items })}
        addLabel="Add Brand"
        emptyItem={{ name: "", logo_url: "", url: "/" }}
        renderItem={(item, update) => (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Brand Name">
              <Input value={item.name} onChange={v => update({ name: v })} placeholder="Mosino" />
            </Field>
            <Field label="Logo Image URL">
              <ImageUploadInput value={item.logo_url} onChange={v => update({ logo_url: v })} placeholder="https://…/logo.png" />
            </Field>
            <Field label="Brand Link">
              <Input value={item.url} onChange={v => update({ url: v })} placeholder="/" />
            </Field>
          </div>
        )}
      />
    </div>
  );
}

// Promo Banners Editor
function PromoBannersEditor({ data, onChange }) {
  return (
    <div className="p-4">
      <p className="text-xs text-slate-500 mb-4">Add up to 3 promotional banners displayed side-by-side.</p>
      <ItemListEditor
        items={(data.items || []).slice(0, 3)}
        onUpdate={items => onChange({ ...data, items })}
        addLabel="Add Banner"
        emptyItem={{ title: "", subtitle: "", image_url: "", bg_color: "#1e293b", cta_text: "Shop Now", url: "/" }}
        renderItem={(item, update) => (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Banner Image URL">
              <ImageUploadInput value={item.image_url} onChange={v => update({ image_url: v })} placeholder="https://…/banner.jpg" />
            </Field>
            <Field label="Background Color">
              <div className="flex gap-2">
                <Input value={item.bg_color} onChange={v => update({ bg_color: v })} placeholder="#1e293b" />
                <input type="color" value={item.bg_color || "#1e293b"} onChange={e => update({ bg_color: e.target.value })} className="w-10 h-9 rounded border border-slate-300 cursor-pointer p-0.5" />
              </div>
            </Field>
            <Field label="Title">
              <Input value={item.title} onChange={v => update({ title: v })} placeholder="Smart Watch Collections" />
            </Field>
            <Field label="Subtitle / CTA Text">
              <Input value={item.subtitle} onChange={v => update({ subtitle: v })} placeholder="Shop Smart Watches →" />
            </Field>
            <Field label="Link URL">
              <Input value={item.url} onChange={v => update({ url: v })} placeholder="/" />
            </Field>
          </div>
        )}
      />
    </div>
  );
}

// Category Sections Editor
function CategorySectionsEditor({ data, onChange, collections }) {
  const sections = data.items || [];
  const updateSections = (items) => onChange({ ...data, items });

  return (
    <div className="p-4">
      <p className="text-xs text-slate-500 mb-4">Each section pulls products from a DB collection and displays them with filter tabs (All / New / Best Sellers / Featured) and a View All link.</p>
      <ItemListEditor
        items={sections}
        onUpdate={updateSections}
        addLabel="Add Category Section"
        emptyItem={{
          title: "", collection: "", enabled: true,
        }}
        renderItem={(section, update) => (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-xs font-bold text-slate-700">Section Settings</p>
              <Toggle value={section.enabled !== false} onChange={v => update({ enabled: v })} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Section Title">
                <Input value={section.title} onChange={v => update({ title: v })} placeholder="Electronics" />
              </Field>
              <Field label="Product Collection" hint="Filters products from this DB collection">
                <div className="flex gap-2">
                  <Input value={section.collection} onChange={v => update({ collection: v })} placeholder="Electronics" />
                  {collections.length > 0 && (
                    <select
                      value={section.collection || ""}
                      onChange={e => update({ collection: e.target.value })}
                      className="text-xs border border-slate-300 rounded-lg px-2 py-2 focus:outline-none focus:border-orange-500 bg-white text-slate-700 max-w-[130px]"
                    >
                      <option value="">Pick…</option>
                      {collections.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  )}
                </div>
              </Field>
            </div>
          </div>
        )}
      />
    </div>
  );
}

// Recommended Editor
function RecommendedEditor({ data, onChange, collections }) {
  return (
    <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
      <Field label="Section Title">
        <Input value={data.title} onChange={v => onChange({ ...data, title: v })} placeholder="Recommended For You" />
      </Field>
      <Field label="Collection Filter (optional)">
        <div className="flex gap-2">
          <Input value={data.collection || ""} onChange={v => onChange({ ...data, collection: v })} placeholder="All collections" />
          {collections.length > 0 && (
            <select value={data.collection || ""} onChange={e => onChange({ ...data, collection: e.target.value })}
              className="text-xs border border-slate-300 rounded-lg px-2 py-2 focus:outline-none focus:border-orange-500 bg-white text-slate-700 max-w-[130px]">
              <option value="">All</option>
              {collections.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
        </div>
      </Field>
      <Field label="Max Products to Show">
        <Input value={data.limit || 10} onChange={v => onChange({ ...data, limit: parseInt(v) || 10 })} type="number" placeholder="10" />
      </Field>
    </div>
  );
}

// Trust Badges Editor
const BADGE_ICONS = ["truck", "shield", "badge-check", "headset"];
function TrustBadgesEditor({ data, onChange }) {
  return (
    <div className="p-4">
      <ItemListEditor
        items={data.items || []}
        onUpdate={items => onChange({ ...data, items })}
        addLabel="Add Trust Badge"
        emptyItem={{ icon: "truck", title: "", subtitle: "" }}
        renderItem={(badge, update) => (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Icon">
              <select value={badge.icon || "truck"} onChange={e => update({ icon: e.target.value })}
                className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-500 bg-white text-slate-800">
                {BADGE_ICONS.map(ic => <option key={ic} value={ic}>{ic}</option>)}
              </select>
            </Field>
            <Field label="Title">
              <Input value={badge.title} onChange={v => update({ title: v })} placeholder="Free Standard Delivery" />
            </Field>
            <Field label="Subtitle" >
              <Input value={badge.subtitle} onChange={v => update({ subtitle: v })} placeholder="On orders above ₹999" />
            </Field>
          </div>
        )}
      />
    </div>
  );
}

// Newsletter Editor
function NewsletterEditor({ data, onChange }) {
  return (
    <div className="p-4 grid grid-cols-1 gap-3">
      <Field label="Title">
        <Input value={data.title} onChange={v => onChange({ ...data, title: v })} placeholder="Sign Up For Our Newsletter" />
      </Field>
      <Field label="Subtitle">
        <Input value={data.subtitle} onChange={v => onChange({ ...data, subtitle: v })} placeholder="Receive our latest updates…" />
      </Field>
    </div>
  );
}

// Navigation Editor
function NavigationEditor({ menu, onChange }) {
  return (
    <div className="space-y-4">
      <p className="text-xs text-slate-500">Manage main navigation items. Badges add colored pills next to the label (e.g. "New", "Sale").</p>
      <ItemListEditor
        items={menu}
        onUpdate={onChange}
        addLabel="Add Menu Item"
        emptyItem={{ label: "", url: "/", badge: "", badge_color: "" }}
        renderItem={(item, update) => (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Label">
              <Input value={item.label} onChange={v => update({ label: v })} placeholder="Shop" />
            </Field>
            <Field label="URL">
              <Input value={item.url} onChange={v => update({ url: v })} placeholder="/" />
            </Field>
            <Field label="Badge Text (optional)">
              <Input value={item.badge} onChange={v => update({ badge: v })} placeholder="New" />
            </Field>
            <Field label="Badge Color">
              <div className="flex gap-2">
                <Input value={item.badge_color} onChange={v => update({ badge_color: v })} placeholder="#0284c7" />
                <input type="color" value={item.badge_color || "#0284c7"} onChange={e => update({ badge_color: e.target.value })} className="w-10 h-9 rounded border border-slate-300 cursor-pointer p-0.5" />
              </div>
            </Field>
          </div>
        )}
      />
    </div>
  );
}

// Top Bar Editor
function TopBarEditor({ data, onChange }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Phone Number">
          <Input value={data.phone} onChange={v => onChange({ ...data, phone: v })} placeholder="+91 9321 234 567" />
        </Field>
        <Field label="Email Address">
          <Input value={data.email} onChange={v => onChange({ ...data, email: v })} type="email" placeholder="support@importtrend.com" />
        </Field>
        <Field label="Announcement Text" hint="Shows in the center of the top bar">
          <Input value={data.notice} onChange={v => onChange({ ...data, notice: v })} placeholder="Free Shipping on orders above ₹999" />
        </Field>
        <Field label="Background Color">
          <div className="flex gap-2">
            <Input value={data.bg_color} onChange={v => onChange({ ...data, bg_color: v })} placeholder="#0f172a" />
            <input type="color" value={data.bg_color || "#0f172a"} onChange={e => onChange({ ...data, bg_color: e.target.value })} className="w-10 h-9 rounded border border-slate-300 cursor-pointer p-0.5" />
          </div>
        </Field>
      </div>
    </div>
  );
}

// Footer Editor — full tabbed editor matching the new footer design
function FooterEditor({ data, onChange }) {
  const [tab, setTab] = useState("brand");
  const u = (patch) => onChange({ ...data, ...patch });

  // ── Social media helpers ──
  const PLATFORM_OPTIONS = [
    { value: "instagram", label: "Instagram" },
    { value: "facebook",  label: "Facebook"  },
    { value: "youtube",   label: "YouTube"   },
    { value: "linkedin",  label: "LinkedIn"  },
    { value: "twitter",   label: "Twitter / X" },
    { value: "whatsapp",  label: "WhatsApp"  },
    { value: "tiktok",    label: "TikTok"    },
    { value: "pinterest", label: "Pinterest" },
    { value: "custom",    label: "Custom"    },
  ];

  // Migrate legacy flat-object social → accounts array
  const rawSocial = data.social || {};
  const social = (() => {
    if (Array.isArray(rawSocial.accounts)) return rawSocial;
    const ORDER = ["linkedin", "facebook", "instagram", "youtube", "twitter", "whatsapp", "pinterest"];
    const accounts = ORDER
      .filter(p => rawSocial[p])
      .map((p, i) => ({ id: `${p}_${i}`, platform: p, url: rawSocial[p], icon_url: "", label: "" }));
    return { follow_us_title: rawSocial.follow_us_title || "", accounts };
  })();
  const updateSocial    = (patch) => onChange({ ...data, social: { ...social, ...patch } });
  const updateAccounts  = (accounts) => updateSocial({ accounts });

  const updateCol = (col, patch) => onChange({ ...data, [col]: { ...(data[col] || {}), ...patch } });

  // Reusable section toggle header
  const SectionToggle = ({ field, label }) => (
    <div className="flex items-center justify-between mb-3">
      <p className="text-xs font-bold text-slate-700">{label}</p>
      <Toggle value={data[field]?.enabled !== false} onChange={v => u({ [field]: { ...(data[field] || {}), enabled: v } })} />
    </div>
  );

  const TABS = [
    { id: "brand", label: "Brand & Contact" },
    { id: "nav", label: "Navigation" },
    { id: "prefooter", label: "Pre-footer Blocks" },
    { id: "seo", label: "SEO Footer" },
  ];

  return (
    <div>
      {/* Inner tabs */}
      <div className="flex border-b border-slate-100 mb-5 overflow-x-auto">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`text-xs font-semibold px-4 py-2.5 border-b-2 whitespace-nowrap transition-colors ${tab === t.id ? "border-orange-500 text-orange-600" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Brand & Contact ── */}
      {tab === "brand" && (
        <div className="space-y-4">
          {/* Logo display mode */}
          <div className="border border-slate-200 rounded-xl p-4 bg-slate-50/50">
            <p className="text-xs font-bold text-slate-600 mb-3">Logo Display Mode</p>
            <div className="flex gap-2 mb-4">
              {[
                { value: "text",  label: "Text Only" },
                { value: "image", label: "Logo Image" },
                { value: "both",  label: "Both" },
              ].map(opt => (
                <button key={opt.value}
                  onClick={() => u({ logo_display_mode: opt.value })}
                  className={`px-4 py-2 rounded-lg text-xs font-semibold border transition-colors ${
                    (data.logo_display_mode || "text") === opt.value
                      ? "bg-orange-500 text-white border-orange-500"
                      : "bg-white text-slate-600 border-slate-200 hover:border-orange-300"
                  }`}>
                  {opt.label}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Logo Text — shown for text / both */}
              {(data.logo_display_mode || "text") !== "image" && (
                <Field label="Logo Text" hint="Brand name in footer (e.g. importrend)">
                  <Input value={data.logo_text || ""} onChange={v => u({ logo_text: v })} placeholder="importrend" />
                </Field>
              )}
              {/* Logo Image — shown for image / both */}
              {(data.logo_display_mode || "text") !== "text" && (
                <Field label="Logo Image" hint="Upload your brand logo for the footer">
                  <ImageUploadInput value={data.footer_logo_url || ""} onChange={v => u({ footer_logo_url: v })} placeholder="Paste URL or upload logo" />
                  {data.footer_logo_url && (
                    <div className="mt-2 flex items-center gap-3 p-2 bg-white border border-slate-200 rounded-lg">
                      <img src={data.footer_logo_url} alt="Logo preview" className="h-8 object-contain" />
                      <span className="text-xs text-slate-500">Preview</span>
                      <button onClick={() => u({ footer_logo_url: "" })} className="ml-auto text-xs text-red-400 hover:text-red-600">Remove</button>
                    </div>
                  )}
                  <div>
                    <p className="text-[10px] text-slate-400 mt-1.5">Logo height in footer</p>
                    <div className="flex gap-2 mt-1">
                      {[24,32,40,48].map(h => (
                        <button key={h} onClick={() => u({ footer_logo_height: h })}
                          className={`px-2 py-1 rounded text-[11px] font-semibold transition-colors ${(data.footer_logo_height||32)===h ? "bg-orange-500 text-white" : "bg-slate-100 text-slate-500"}`}>
                          {h}px
                        </button>
                      ))}
                    </div>
                  </div>
                </Field>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Brand Tagline">
              <Input value={data.tagline || ""} onChange={v => u({ tagline: v })} placeholder="India's first AI-powered B2B…" />
            </Field>
            <Field label="View More Text">
              <Input value={data.view_more_text || ""} onChange={v => u({ view_more_text: v })} placeholder="View More" />
            </Field>
            <Field label="View More URL">
              <Input value={data.view_more_url || ""} onChange={v => u({ view_more_url: v })} placeholder="/about" />
            </Field>
            <Field label="Office Timings" hint="Shown below Connect With Us heading">
              <textarea value={data.office_timings || ""} onChange={e => u({ office_timings: e.target.value })} rows={2}
                placeholder={"Office Timings - 09:00 AM to 6:00 PM\nMonday - Saturday"}
                className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-500 resize-none" />
            </Field>
            <Field label="Email">
              <Input value={data.email || ""} onChange={v => u({ email: v })} placeholder="contact@importrend.com" />
            </Field>
            <Field label="Address" hint="Shown in 'Our Office' card on mobile footer">
              <textarea
                value={data.address || ""}
                onChange={e => u({ address: e.target.value })}
                rows={2}
                placeholder="79/3, 2nd Floor, Benson Town, Bengaluru - 560046"
                className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-500 resize-none"
              />
            </Field>
            <Field label="Copyright Line">
              <Input value={data.copyright || ""} onChange={v => u({ copyright: v })} placeholder="© 2025, Importrend. All rights reserved." />
            </Field>
            <div className="flex items-center gap-2 pt-1">
              <Toggle value={data.show_payment_icons !== false} onChange={v => u({ show_payment_icons: v })} label="Show Partner Logos" />
            </div>
          </div>

          {/* Phones */}
          <div>
            <p className="text-xs font-bold text-slate-600 mb-3">Phone Numbers</p>
            <ItemListEditor
              items={data.phones || []}
              onUpdate={phones => u({ phones })}
              addLabel="+ Add Phone"
              emptyItem={{ label: "", number: "" }}
              renderItem={(item, update) => (
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Label (optional)"><Input value={item.label} onChange={v => update({ label: v })} placeholder="Order Queries:" /></Field>
                  <Field label="Phone Number"><Input value={item.number} onChange={v => update({ number: v })} placeholder="+91 9876543210" /></Field>
                </div>
              )}
            />
          </div>

          {/* Social Media */}
          <div className="border border-slate-200 rounded-xl p-4 bg-slate-50/50">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-bold text-slate-600">Social Media Links</p>
              <button
                onClick={() => updateAccounts([...social.accounts, { id: uid(), platform: "instagram", url: "", icon_url: "", label: "" }])}
                className="flex items-center gap-1 text-xs font-semibold text-orange-600 hover:text-orange-700"
              >
                <Plus className="w-3 h-3" /> Add Account
              </button>
            </div>

            {/* Follow Us title */}
            <div className="mb-3">
              <Field label='"Follow Us" Section Title'>
                <Input value={social.follow_us_title || ""} onChange={v => updateSocial({ follow_us_title: v })} placeholder="Follow Us" />
              </Field>
            </div>

            {/* Account list */}
            <div className="space-y-3">
              {(social.accounts || []).map((acc, idx) => (
                <div key={acc.id || idx} className="bg-white border border-slate-200 rounded-lg p-3 space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    {/* Platform */}
                    <Field label="Platform">
                      <select
                        value={acc.platform || "custom"}
                        onChange={e => { const a=[...social.accounts]; a[idx]={...acc,platform:e.target.value}; updateAccounts(a); }}
                        className="w-full py-2 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-orange-500 bg-white text-slate-800"
                      >
                        {PLATFORM_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                      </select>
                    </Field>
                    {/* Label */}
                    <Field label="Label (optional)">
                      <Input value={acc.label||""} onChange={v=>{ const a=[...social.accounts]; a[idx]={...acc,label:v}; updateAccounts(a); }} placeholder="e.g. Main, Fashion" />
                    </Field>
                  </div>

                  {/* URL */}
                  <Field label="Profile URL">
                    <Input value={acc.url||""} onChange={v=>{ const a=[...social.accounts]; a[idx]={...acc,url:v}; updateAccounts(a); }} placeholder="https://instagram.com/youraccount" />
                  </Field>

                  {/* Custom icon + Remove */}
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <Field label="Custom Icon (optional — overrides platform icon)">
                        <ImageUploadInput
                          value={acc.icon_url||""}
                          onChange={v=>{ const a=[...social.accounts]; a[idx]={...acc,icon_url:v}; updateAccounts(a); }}
                          placeholder="Upload or paste icon URL"
                        />
                      </Field>
                      {acc.icon_url && (
                        <div className="mt-1 flex items-center gap-2">
                          <img src={acc.icon_url} alt="icon preview" className="w-6 h-6 object-contain rounded" />
                          <span className="text-[10px] text-slate-400">Preview</span>
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => updateAccounts(social.accounts.filter((_,i)=>i!==idx))}
                      className="mb-0.5 flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 text-slate-400 hover:text-red-500 hover:border-red-300 transition-colors"
                      title="Remove"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
              {(social.accounts||[]).length === 0 && (
                <div className="text-center py-5 text-xs text-slate-400 border-2 border-dashed border-slate-200 rounded-lg">
                  No social accounts yet. Click <strong>Add Account</strong> to start.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Navigation ── */}
      {tab === "nav" && (
        <div className="space-y-6">
          {[["links_col1", "Column 1 (HOW IT WORKS)"], ["links_col2", "Column 2 (GET TO KNOW US)"], ["links_col3", "Column 3 (QUICK LINKS)"]].map(([col, label]) => (
            <div key={col}>
              <p className="text-xs font-bold text-slate-600 mb-2">{label}</p>
              <Field label="Column Title">
                <Input value={data[col]?.title || ""} onChange={v => updateCol(col, { title: v })} placeholder="HOW IT WORKS" />
              </Field>
              <div className="mt-3">
                <ItemListEditor
                  items={data[col]?.links || []}
                  onUpdate={links => updateCol(col, { links })}
                  addLabel="Add Link"
                  emptyItem={{ label: "", url: "/" }}
                  renderItem={(link, update) => (
                    <div className="grid grid-cols-2 gap-2">
                      <Field label="Label"><Input value={link.label} onChange={v => update({ label: v })} placeholder="About Us" /></Field>
                      <Field label="URL"><Input value={link.url} onChange={v => update({ url: v })} placeholder="/about" /></Field>
                    </div>
                  )}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Pre-footer Blocks ── */}
      {tab === "prefooter" && (
        <div className="space-y-6">
          {/* Why Section */}
          <div className="border border-slate-200 rounded-xl p-4">
            <SectionToggle field="why_section" label="Why Section (Feature Cards)" />
            <div className="space-y-3">
              <Field label="Title">
                <Input value={data.why_section?.title || ""} onChange={v => u({ why_section: { ...(data.why_section || {}), title: v } })} placeholder="WHY IMPORTREND IS INDIA'S #1…" />
              </Field>
              <Field label="Description">
                <textarea value={data.why_section?.description || ""} onChange={e => u({ why_section: { ...(data.why_section || {}), description: e.target.value } })} rows={2}
                  className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-500 resize-none" placeholder="At Importrend, we simplify…" />
              </Field>
              <p className="text-xs font-bold text-slate-600 mb-1">Feature Cards</p>
              <ItemListEditor
                items={data.why_section?.items || []}
                onUpdate={items => u({ why_section: { ...(data.why_section || {}), items } })}
                addLabel="+ Add Feature Card"
                emptyItem={{ title: "", description: "" }}
                renderItem={(item, update) => (
                  <div className="space-y-2">
                    <Field label="Title"><Input value={item.title} onChange={v => update({ title: v })} placeholder="Direct Sourcing, No Middlemen" /></Field>
                    <Field label="Description">
                      <textarea value={item.description || ""} onChange={e => update({ description: e.target.value })} rows={2}
                        className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-500 resize-none" placeholder="Get connected directly…" />
                    </Field>
                  </div>
                )}
              />
            </div>
          </div>

          {/* Quick Searches */}
          <div className="border border-slate-200 rounded-xl p-4">
            <SectionToggle field="quick_searches" label="Quick Searches (Tag Groups)" />
            <ItemListEditor
              items={data.quick_searches?.groups || []}
              onUpdate={groups => u({ quick_searches: { ...(data.quick_searches || {}), groups } })}
              addLabel="+ Add Search Group"
              emptyItem={{ title: "", tags: [] }}
              renderItem={(group, update) => (
                <div className="space-y-2">
                  <Field label="Group Title"><Input value={group.title} onChange={v => update({ title: v })} placeholder="Popular Categories" /></Field>
                  <Field label="Tags (comma-separated)">
                    <textarea
                      value={(group.tags || []).join(", ")}
                      onChange={e => update({ tags: e.target.value.split(",").map(t => t.trim()).filter(Boolean) })}
                      rows={2} placeholder="Home & Kitchen, Lifestyle, Fashion…"
                      className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-500 resize-none" />
                  </Field>
                </div>
              )}
            />
          </div>

          {/* Asset-Lite Section */}
          <div className="border border-slate-200 rounded-xl p-4">
            <SectionToggle field="asset_lite_section" label="Asset-Lite Section (4 Cards)" />
            <div className="space-y-3">
              <Field label="Title"><Input value={data.asset_lite_section?.title || ""} onChange={v => u({ asset_lite_section: { ...(data.asset_lite_section || {}), title: v } })} placeholder="ASSET-LITE SOURCING FOR…" /></Field>
              <Field label="Description">
                <textarea value={data.asset_lite_section?.description || ""} onChange={e => u({ asset_lite_section: { ...(data.asset_lite_section || {}), description: e.target.value } })} rows={2}
                  className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-500 resize-none" />
              </Field>
              <Field label="Card Texts (one per line)" hint="Each line becomes a separate card">
                <textarea
                  value={(data.asset_lite_section?.items || []).join("\n")}
                  onChange={e => u({ asset_lite_section: { ...(data.asset_lite_section || {}), items: e.target.value.split("\n").filter(Boolean) } })}
                  rows={4} placeholder={"Get direct access to international factories\nReceive quotations within 24 hours"}
                  className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-500 resize-none" />
              </Field>
            </div>
          </div>

          {/* Story Section */}
          <div className="border border-slate-200 rounded-xl p-4">
            <SectionToggle field="story_section" label="Story Section (Paragraphs)" />
            <div className="space-y-3">
              <Field label="Title"><Input value={data.story_section?.title || ""} onChange={v => u({ story_section: { ...(data.story_section || {}), title: v } })} placeholder="FROM AN IDEA TO…" /></Field>
              <Field label="Paragraphs (one per line)">
                <textarea
                  value={(data.story_section?.paragraphs || []).join("\n\n")}
                  onChange={e => u({ story_section: { ...(data.story_section || {}), paragraphs: e.target.value.split("\n\n").filter(Boolean) } })}
                  rows={6} placeholder="Built on the vision…"
                  className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-500 resize-none" />
              </Field>
            </div>
          </div>

          {/* Contact Section */}
          <div className="border border-slate-200 rounded-xl p-4">
            <SectionToggle field="contact_section" label="Contact & Support Block" />
            <div className="space-y-3">
              <Field label="Title"><Input value={data.contact_section?.title || ""} onChange={v => u({ contact_section: { ...(data.contact_section || {}), title: v } })} placeholder="CONTACT & SUPPORT" /></Field>
              <Field label="Description">
                <textarea value={data.contact_section?.description || ""} onChange={e => u({ contact_section: { ...(data.contact_section || {}), description: e.target.value } })} rows={2}
                  className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-500 resize-none" />
              </Field>
              <p className="text-xs font-bold text-slate-600 mt-2">Highlight Items</p>
              <ItemListEditor
                items={data.contact_section?.items || []}
                onUpdate={items => u({ contact_section: { ...(data.contact_section || {}), items } })}
                addLabel="+ Add Item"
                emptyItem={{ label: "", value: "" }}
                renderItem={(item, update) => (
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Label"><Input value={item.label} onChange={v => update({ label: v })} placeholder="Customer Support" /></Field>
                    <Field label="Value"><Input value={item.value} onChange={v => update({ value: v })} placeholder="Guidance for sourcing…" /></Field>
                  </div>
                )}
              />
            </div>
          </div>
        </div>
      )}

      {/* ── SEO Footer ── */}
      {tab === "seo" && (
        <div className="border border-slate-200 rounded-xl p-4 space-y-3">
          <SectionToggle field="seo_section" label="SEO Text Footer (very bottom)" />
          <Field label="Heading">
            <Input value={data.seo_section?.title || ""} onChange={v => u({ seo_section: { ...(data.seo_section || {}), title: v } })} placeholder="GLOBAL SOURCING MADE SIMPLE WITH IMPORTREND" />
          </Field>
          <Field label="Paragraphs (separate with blank line)" hint="Each blank-line separated block = one paragraph">
            <textarea
              value={(data.seo_section?.paragraphs || []).join("\n\n")}
              onChange={e => u({ seo_section: { ...(data.seo_section || {}), paragraphs: e.target.value.split("\n\n").filter(Boolean) } })}
              rows={6} placeholder="Welcome to Importrend…"
              className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-500 resize-none" />
          </Field>
        </div>
      )}
    </div>
  );
}

// ── Categories Showcase Editor ─────────────────────────────────────────────
function CategoriesShowcaseEditor({ data, onChange }) {
  return (
    <div className="p-4 space-y-4">
      <Field label="Section Title">
        <Input value={data.title} onChange={v => onChange({ ...data, title: v })} placeholder="Categories" />
      </Field>
      <div>
        <p className="text-xs font-semibold text-slate-600 mb-3">Category Cards ({(data.items || []).length})</p>
        <ItemListEditor
          items={data.items || []}
          onUpdate={items => onChange({ ...data, items })}
          addLabel="+ Add Category Card"
          emptyItem={{ name: "", slug: "", image: "", tagline: "", icon: "", bg_color: "#f1f5f9" }}
          renderItem={(item, update) => (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Category Name">
                <Input value={item.name} onChange={v => { const slug = item.slug || v.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, ""); update({ name: v, slug }); }} placeholder="Arts & Crafts" />
              </Field>
              <Field label="URL Slug" hint="Navigates to /c/{slug}">
                <Input value={item.slug} onChange={v => update({ slug: v.toLowerCase().replace(/[^a-z0-9-]+/g, "-") })} placeholder="arts-crafts" />
              </Field>
              <Field label="Card Image URL" hint="Portrait image, ~600×720px">
                <ImageUploadInput value={item.image} onChange={v => update({ image: v })} placeholder="https://…/image.jpg" />
              </Field>
              <Field label="Background Color (shown if no image)">
                <div className="flex gap-2">
                  <Input value={item.bg_color} onChange={v => update({ bg_color: v })} placeholder="#f1f5f9" />
                  <input type="color" value={item.bg_color || "#f1f5f9"} onChange={e => update({ bg_color: e.target.value })} className="w-10 h-9 rounded border border-slate-300 cursor-pointer p-0.5" />
                </div>
              </Field>
              <Field label="Tagline (overlay text)" hint="Space-separated words, last word italicised in yellow">
                <Input value={item.tagline} onChange={v => update({ tagline: v })} placeholder="Create Play Imagine" />
              </Field>
              <Field label="Icon Emoji" hint="Shown in bottom bar">
                <Input value={item.icon} onChange={v => update({ icon: v })} placeholder="🎨" />
              </Field>
            </div>
          )}
        />
      </div>
    </div>
  );
}

// ── B2B Section Editor ─────────────────────────────────────────────────────
function B2BSectionEditor({ data, onChange }) {
  const u = (patch) => onChange({ ...data, ...patch });
  return (
    <div className="p-4 space-y-6">
      {/* Header */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Section Label (small caps above heading)">
          <Input value={data.label} onChange={v => u({ label: v })} placeholder="WHO BUYS FROM US • WHY IMPORTREND" />
        </Field>
        <Field label="Main Heading">
          <Input value={data.title} onChange={v => u({ title: v })} placeholder="Wholesale for every kind of buyer." />
        </Field>
        <Field label="CTA Button Text">
          <Input value={data.cta_text} onChange={v => u({ cta_text: v })} placeholder="Open a B2B account" />
        </Field>
        <Field label="CTA Button URL">
          <Input value={data.cta_url} onChange={v => u({ cta_url: v })} placeholder="/register" />
        </Field>
      </div>

      {/* Buyer Types */}
      <div>
        <p className="text-xs font-semibold text-slate-600 mb-3">Buyer Type Cards</p>
        <ItemListEditor
          items={data.buyer_types || []}
          onUpdate={buyer_types => u({ buyer_types })}
          addLabel="+ Add Buyer Type"
          emptyItem={{ icon: "🏪", title: "", description: "" }}
          renderItem={(item, update) => (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label="Icon Emoji"><Input value={item.icon} onChange={v => update({ icon: v })} placeholder="🏪" /></Field>
              <Field label="Title"><Input value={item.title} onChange={v => update({ title: v })} placeholder="Retailers & resellers" /></Field>
              <Field label="Description"><Input value={item.description} onChange={v => update({ description: v })} placeholder="Stock your shelves…" /></Field>
            </div>
          )}
        />
      </div>

      {/* USPs */}
      <div>
        <p className="text-xs font-semibold text-slate-600 mb-3">USP Bar (dark background)</p>
        <ItemListEditor
          items={data.usps || []}
          onUpdate={usps => u({ usps })}
          addLabel="+ Add USP"
          emptyItem={{ icon: "✅", title: "", description: "" }}
          renderItem={(item, update) => (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label="Icon Emoji"><Input value={item.icon} onChange={v => update({ icon: v })} placeholder="✅" /></Field>
              <Field label="Title"><Input value={item.title} onChange={v => update({ title: v })} placeholder="No MOQ. Ever." /></Field>
              <Field label="Description"><Input value={item.description} onChange={v => update({ description: v })} placeholder="Buy 1 or 10,000…" /></Field>
            </div>
          )}
        />
        <div className="mt-3">
          <Field label="USP Bar Footer Text">
            <Input value={data.usps_footer} onChange={v => u({ usps_footer: v })} placeholder="Direct from manufacturing partners…" />
          </Field>
        </div>
      </div>

      {/* FAQ */}
      <div>
        <p className="text-xs font-semibold text-slate-600 mb-3">FAQ Section</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <Field label="Label (above heading)"><Input value={data.faq_label} onChange={v => u({ faq_label: v })} placeholder="HERE TO HELP" /></Field>
          <Field label="Heading"><Input value={data.faq_heading} onChange={v => u({ faq_heading: v })} placeholder="A few questions. Clear answers." /></Field>
          <Field label="Description"><Input value={data.faq_description} onChange={v => u({ faq_description: v })} placeholder="Everything you need to know…" /></Field>
          <Field label="Support Email"><Input value={data.support_email} onChange={v => u({ support_email: v })} placeholder="support@importrend.com" /></Field>
          <Field label="Support Button Text"><Input value={data.support_button_text} onChange={v => u({ support_button_text: v })} placeholder="Help & support" /></Field>
          <Field label="Support Button URL"><Input value={data.support_button_url} onChange={v => u({ support_button_url: v })} placeholder="/contact" /></Field>
        </div>
        <p className="text-xs font-semibold text-slate-600 mb-3">FAQ Items</p>
        <ItemListEditor
          items={data.faq_items || []}
          onUpdate={faq_items => u({ faq_items })}
          addLabel="+ Add FAQ"
          emptyItem={{ question: "", answer: "" }}
          renderItem={(item, update) => (
            <div className="space-y-3">
              <Field label="Question"><Input value={item.question} onChange={v => update({ question: v })} placeholder="What is the minimum order quantity?" /></Field>
              <Field label="Answer">
                <textarea value={item.answer || ""} onChange={e => update({ answer: e.target.value })} rows={2} placeholder="There is no minimum…"
                  className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:border-orange-500 bg-white resize-none" />
              </Field>
            </div>
          )}
        />
      </div>
    </div>
  );
}

// ── Section Configs ────────────────────────────────────────────────────────
const SECTION_META = {
  hero: { label: "Hero Banner", icon: Monitor },
  category_grid: { label: "Category Grid", icon: Layout },
  brands: { label: "Brand Logos", icon: Star },
  promo_banners: { label: "Promotional Banners", icon: Tag },
  category_sections: { label: "Category Product Sections", icon: Package },
  categories_showcase: { label: "Categories Showcase", icon: Grid },
  b2b_section: { label: "B2B / Wholesale Section", icon: Users },
  recommended: { label: "Recommended For You", icon: CheckCircle },
  trust_badges: { label: "Trust Badges", icon: Shield },
  newsletter: { label: "Newsletter Signup", icon: Mail },
};

// Keys that belong to the new-design sections (use NewSectionContentRenderer)
const NEW_SECTION_KEYS = [
  "category_icons_row", "secondary_slider", "shop_by_price", "flash_deals",
  "promo_banners_new", "new_arrivals", "featured_categories", "why_shop",
  "top_deals_week", "reseller_app", "customer_reviews",
];

// Combined metadata for all sections (legacy + new)
const UNIFIED_SECTION_META = {
  hero:                { label: "Hero Banner",                   icon: Monitor,     type: "legacy" },
  category_grid:       { label: "Category Grid",                 icon: Layout,      type: "legacy" },
  brands:              { label: "Brand Logos",                   icon: Star,        type: "legacy" },
  promo_banners:       { label: "Promotional Banners",           icon: Tag,         type: "legacy" },
  category_sections:   { label: "Category Product Sections",     icon: Package,     type: "legacy" },
  categories_showcase: { label: "Categories Showcase",           icon: Grid,        type: "legacy" },
  b2b_section:         { label: "B2B / Wholesale Section",       icon: Users,       type: "legacy" },
  recommended:         { label: "Recommended For You",           icon: CheckCircle, type: "legacy" },
  trust_badges:        { label: "Trust Badges",                  icon: Shield,      type: "legacy" },
  newsletter:          { label: "Newsletter Signup",             icon: Mail,        type: "legacy" },
  category_icons_row:  { label: "Category Icons Row",            icon: Grid,        type: "new" },
  secondary_slider:    { label: "Promotional Banner Slideshow",  icon: Film,        type: "new" },
  shop_by_price:       { label: "Shop by Price",                 icon: Zap,         type: "new" },
  flash_deals:         { label: "Flash Deals",                   icon: Zap,         type: "new" },
  promo_banners_new:   { label: "Promo Banners (New Design)",    icon: ShoppingBag, type: "new" },
  new_arrivals:        { label: "New Arrivals",                  icon: Star,        type: "new" },
  featured_categories: { label: "Featured Categories",           icon: Layers,      type: "new" },
  why_shop:            { label: "Why Shop with IMPORTREND",      icon: ShoppingBag, type: "new" },
  top_deals_week:      { label: "Top Deals This Week",           icon: Star,        type: "new" },
  reseller_app:        { label: "Reseller + App",                icon: Smartphone,  type: "new" },
  customer_reviews:    { label: "Customer Reviews",              icon: Heart,       type: "new" },
};

const TITLED_KEYS = ["new_arrivals", "featured_categories", "flash_deals", "top_deals_week", "customer_reviews", "why_shop"];

const DEFAULT_UNIFIED_ORDER = [
  "hero", "category_icons_row", "secondary_slider", "category_grid",
  "shop_by_price", "flash_deals", "brands", "promo_banners", "promo_banners_new",
  "new_arrivals", "category_sections", "featured_categories", "why_shop",
  "top_deals_week", "b2b_section", "reseller_app", "categories_showcase",
  "recommended", "trust_badges", "newsletter", "customer_reviews",
];

// ── Main Component ─────────────────────────────────────────────────────────
export default function HomepageBuilder() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [activeTab, setActiveTab] = useState("sections");
  const [expandedSection, setExpandedSection] = useState(null);
  const [collections, setCollections] = useState([]);
  const [categories, setCategories] = useState([]);

  const token = localStorage.getItem("it_token");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [hpRes, colsRes, catsRes] = await Promise.all([
        axios.get(`${API}/homepage`),
        axios.get(`${API}/collections/public`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/categories`, { headers: { Authorization: `Bearer ${localStorage.getItem("it_token")}` } }).catch(() => ({ data: [] })),
      ]);
      setConfig(hpRes.data);
      // Normalize collections (API returns plain strings or objects)
      const rawCols = colsRes.data || [];
      setCollections(rawCols.map(c => typeof c === "string" ? { name: c, slug: c.toLowerCase().replace(/\s+/g, "-") } : c));
      setCategories(catsRes.data || []);
      setDirty(false);
    } catch {
      toast.error("Failed to load homepage config");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/homepage`, config, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Homepage saved successfully");
      setDirty(false);
    } catch {
      toast.error("Failed to save homepage");
    } finally {
      setSaving(false);
    }
  };

  const updateSection = (key, data) => {
    setConfig(prev => ({ ...prev, [key]: data }));
    setDirty(true);
  };

  const isCustom = (key) => key.startsWith("custom:");
  const customId = (key) => key.slice(7);
  const getSec = (key) => isCustom(key)
    ? ((config?.custom_sections || []).find(s => s.id === customId(key)) || {})
    : (config?.[key] || {});
  const setSec = (key, data) => {
    if (isCustom(key)) {
      setConfig(prev => ({ ...prev, custom_sections: (prev.custom_sections || []).map(s => s.id === customId(key) ? data : s) }));
    } else {
      setConfig(prev => ({ ...prev, [key]: data }));
    }
    setDirty(true);
  };
  const patchSec = (key, fields) => setSec(key, { ...getSec(key), ...fields });
  const toggleSection = (key) => patchSec(key, { enabled: getSec(key).enabled === false });

  const addCustomSection = (type) => {
    const id = Math.random().toString(36).slice(2, 8);
    const base = type === "category_cards"
      ? { type, id, title: "New cards section", enabled: true, cards: [{ title: "Card title", link_url: "", fill: "manual", items: [] }] }
      : { type, id, title: "New tile section", enabled: true, feature: { title: "", link_url: "", image_url: "" }, rows: [{ items: [] }] };
    setConfig(prev => ({
      ...prev,
      custom_sections: [...(prev.custom_sections || []), base],
      unified_sections_order: [...(prev.unified_sections_order || DEFAULT_UNIFIED_ORDER), `custom:${id}`],
    }));
    setExpandedSection(`custom:${id}`);
    setDirty(true);
  };
  const removeCustomSection = (key) => {
    if (!window.confirm("Delete this section? This cannot be undone.")) return;
    setConfig(prev => ({
      ...prev,
      custom_sections: (prev.custom_sections || []).filter(s => s.id !== customId(key)),
      unified_sections_order: (prev.unified_sections_order || DEFAULT_UNIFIED_ORDER).filter(k => k !== key),
    }));
    setExpandedSection(null);
    setDirty(true);
  };

  const moveSection = (key, dir) => {
    const order = [...(config.sections_order || [])];
    const idx = order.indexOf(key);
    if (idx < 0) return;
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= order.length) return;
    [order[idx], order[newIdx]] = [order[newIdx], order[idx]];
    setConfig(prev => ({ ...prev, sections_order: order }));
    setDirty(true);
  };

  // Unified order: moves any section (old or new) within the combined list
  const moveUnifiedSection = (idx, dir) => {
    const order = [...(config.unified_sections_order || DEFAULT_UNIFIED_ORDER)];
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= order.length) return;
    [order[idx], order[newIdx]] = [order[newIdx], order[idx]];
    setConfig(prev => ({ ...prev, unified_sections_order: order }));
    setDirty(true);
  };

  const renderSectionEditor = (key) => {
    if (!config) return null;
    if (isCustom(key)) {
      return <CustomSectionEditor section={getSec(key)} onChange={d => setSec(key, d)} collections={collections} />;
    }
    const s = config[key] || {};

    // New-design sections → delegate to the shared content renderer
    if (NEW_SECTION_KEYS.includes(key)) {
      return (
        <div className="p-4">
          <NewSectionContentRenderer
            sectionKey={key}
            config={config}
            onChange={d => {
              setConfig(prev => ({ ...prev, ...d }));
              setDirty(true);
            }}
            categories={categories}
            collections={collections}
          />
        </div>
      );
    }

    switch (key) {
      case "hero": return <HeroEditor data={s} onChange={d => updateSection(key, d)} />;
      case "category_grid": return <CategoryGridEditor data={s} onChange={d => updateSection(key, d)} collections={collections} />;
      case "brands": return <BrandsEditor data={s} onChange={d => updateSection(key, d)} />;
      case "promo_banners": return <PromoBannersEditor data={s} onChange={d => updateSection(key, d)} />;
      case "category_sections": return <CategorySectionsEditor data={s} onChange={d => updateSection(key, d)} collections={collections} />;
      case "recommended": return <RecommendedEditor data={s} onChange={d => updateSection(key, d)} collections={collections} />;
      case "categories_showcase": return <CategoriesShowcaseEditor data={s} onChange={d => updateSection(key, d)} />;
      case "b2b_section": return <B2BSectionEditor data={s} onChange={d => updateSection(key, d)} />;
      case "trust_badges": return <TrustBadgesEditor data={s} onChange={d => updateSection(key, d)} />;
      case "newsletter": return <NewsletterEditor data={s} onChange={d => updateSection(key, d)} />;
      default: return <p className="p-4 text-xs text-slate-400">No editor for this section.</p>;
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[300px]">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500" />
      </div>
    );
  }

  const unifiedOrder = config?.unified_sections_order || DEFAULT_UNIFIED_ORDER;

  return (
    <div className="p-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-orange-100 rounded-lg flex items-center justify-center">
            <Home className="w-5 h-5 text-orange-600" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-slate-900">Homepage Builder</h1>
            <p className="text-xs text-slate-500">Configure sections, navigation and footer</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <a href="/" target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs text-slate-600 border border-slate-300 hover:border-slate-500 transition-colors">
            <Eye className="w-3.5 h-3.5" /> Preview
          </a>
          <button onClick={load} className="text-slate-400 hover:text-slate-700 transition-colors px-2">
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            data-testid="homepage-save-btn"
            onClick={save}
            disabled={!dirty || saving}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${
              dirty ? "bg-orange-500 hover:bg-orange-600 text-white shadow-sm" : "bg-slate-100 text-slate-400 cursor-not-allowed"
            }`}
          >
            {saving ? <div className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>

      {/* Tabs — "New Sections" removed; all sections unified in "Sections" tab */}
      <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-1 mb-6 w-fit overflow-x-auto">
        {[
          { id: "sections", label: "Sections", icon: Layout },
          { id: "header", label: "Header & Nav", icon: PanelTop },
          { id: "navigation", label: "Navigation", icon: Menu },
          { id: "topbar", label: "Top Bar", icon: Tag },
          { id: "footer", label: "Footer", icon: Settings },
          { id: "dimensions", label: "Creative Sizes", icon: Image },
        ].map(tab => {
          const Icon = tab.icon;
          return (
            <button key={tab.id}
              data-testid={`hp-tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium transition-all flex-shrink-0 ${
                activeTab === tab.id ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
              }`}>
              <Icon className="w-3.5 h-3.5" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {dirty && (
        <div className="mb-4 flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          <span className="w-1.5 h-1.5 bg-amber-500 rounded-full" />
          Unsaved changes — click Save to publish to homepage
        </div>
      )}

      {/* ── Sections Tab — unified (all legacy + new sections in one list) ── */}
      {activeTab === "sections" && (
        <div>
          <div className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 mb-4 flex items-center gap-2 text-xs text-slate-600">
            <span className="bg-orange-100 text-orange-600 font-black text-[9px] px-1.5 py-0.5 rounded-full uppercase">NEW</span>
            sections are part of the new homepage design.
            Use <strong className="mx-0.5">↑↓</strong> to reorder, the toggle to show/hide, and <strong className="mx-0.5">Edit</strong> to configure content.
          </div>
          <div className="space-y-2">
            {unifiedOrder.map((key, idx) => {
              const meta = isCustom(key)
                ? { label: getSec(key).title || (getSec(key).type === "category_cards" ? "Category Cards" : "Tile Showcase"), icon: Layout, type: "new" }
                : UNIFIED_SECTION_META[key];
              if (!meta) return null;
              if (isCustom(key) && !getSec(key).id) return null;
              const sectionData = getSec(key);
              const isOpen = expandedSection === key;
              return (
                <div key={key}>
                  <SectionCard
                    title={meta.label}
                    icon={meta.icon}
                    badge={meta.type === "new" ? "NEW" : null}
                    enabled={sectionData.enabled !== false}
                    onToggle={() => toggleSection(key)}
                    onEdit={() => setExpandedSection(isOpen ? null : key)}
                    onMoveUp={() => moveUnifiedSection(idx, -1)}
                    onMoveDown={() => moveUnifiedSection(idx, 1)}
                    isFirst={idx === 0}
                    isLast={idx === unifiedOrder.length - 1}
                    isOpen={isOpen}
                  />
                  <div className="flex flex-wrap items-center gap-3 px-4 py-1.5 text-[11px] text-slate-500">
                    <span>Show on</span>
                    <DeviceSelect value={sectionData.device} onChange={v => patchSec(key, { device: v })} />
                    {isCustom(key) && <button type="button" onClick={() => removeCustomSection(key)} className="ml-auto text-red-500 hover:text-red-700">Delete section</button>}
                  </div>
                  {isOpen && (
                    <div className="border border-t-0 border-orange-200 rounded-b-xl bg-white overflow-hidden">
                      {TITLED_KEYS.includes(key) && (
                        <div className="flex flex-wrap gap-5 px-4 pt-3 text-sm text-slate-700">
                          <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" className="w-4 h-4 accent-orange-500" checked={sectionData.show_title !== false} onChange={e => patchSec(key, { show_title: e.target.checked })} /> Show title on storefront</label>
                          <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" className="w-4 h-4 accent-orange-500" checked={sectionData.show_subtitle !== false} onChange={e => patchSec(key, { show_subtitle: e.target.checked })} /> Show subtitle on storefront</label>
                        </div>
                      )}
                      {renderSectionEditor(key)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-4" data-testid="add-custom-section">
            <button type="button" onClick={() => addCustomSection("tile_showcase")} className="flex items-center justify-center gap-1.5 text-xs font-semibold text-orange-600 bg-orange-50 hover:bg-orange-100 px-3 py-3 rounded-xl border border-orange-200 border-dashed">+ Add Tile Showcase section</button>
            <button type="button" onClick={() => addCustomSection("category_cards")} className="flex items-center justify-center gap-1.5 text-xs font-semibold text-orange-600 bg-orange-50 hover:bg-orange-100 px-3 py-3 rounded-xl border border-orange-200 border-dashed">+ Add Category Cards section</button>
          </div>
        </div>
      )}

      {/* ── Header & Nav Tab ── */}
      {activeTab === "header" && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-5 py-3.5 border-b border-slate-100 bg-slate-50">
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Header & Navigation</p>
            <p className="text-xs text-slate-400 mt-0.5">Configure the header layout, CTA button, nav bar items, and mega menu categories</p>
          </div>
          <div className="p-5">
            <HeaderAdmin
              data={config || {}}
              onChange={d => { setConfig(d); setDirty(true); }}
            />
          </div>
        </div>
      )}

      {/* ── Navigation Tab ── */}
      {activeTab === "navigation" && (
        <div className="space-y-4">
          {/* Nav bar enable/disable */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="px-5 py-3.5 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
              <div>
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Navigation Bar</p>
                <p className="text-xs text-slate-400 mt-0.5">The dark bar with Category button and menu links below the main header</p>
              </div>
              <Toggle
                value={config?.nav_bar?.enabled !== false}
                onChange={v => {
                  setConfig(prev => ({ ...prev, nav_bar: { ...(prev.nav_bar || {}), enabled: v } }));
                  setDirty(true);
                }}
              />
            </div>
          </div>

          {/* Menu items editor */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="px-5 py-3.5 border-b border-slate-100 bg-slate-50">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Menu Links</p>
            </div>
            <div className="p-5">
              <NavigationEditor
                menu={config?.main_menu || []}
                onChange={menu => {
                  setConfig(prev => ({ ...prev, main_menu: menu }));
                  setDirty(true);
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* ── Top Bar Tab ── */}
      {activeTab === "topbar" && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-5 py-3.5 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Top Announcement Bar</p>
            <Toggle
              value={config?.top_bar?.enabled !== false}
              onChange={v => {
                setConfig(prev => ({ ...prev, top_bar: { ...(prev.top_bar || {}), enabled: v } }));
                setDirty(true);
              }}
            />
          </div>
          <div className="p-5">
            <TopBarEditor
              data={config?.top_bar || {}}
              onChange={d => {
                setConfig(prev => ({ ...prev, top_bar: d }));
                setDirty(true);
              }}
            />
          </div>
        </div>
      )}

      {/* ── Footer Tab ── */}
      {activeTab === "footer" && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-5 py-3.5 border-b border-slate-100 bg-slate-50">
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Footer Settings</p>
            <p className="text-xs text-slate-400 mt-0.5">Brand, contact, navigation links, pre-footer blocks and SEO text</p>
          </div>
          <div className="p-5">
            <FooterEditor
              data={config?.footer || {}}
              onChange={d => {
                setConfig(prev => ({ ...prev, footer: d }));
                setDirty(true);
              }}
            />
          </div>
        </div>
      )}

      {/* ── Dimensions Guide Tab ── */}
      {activeTab === "dimensions" && (
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-xl px-5 py-4">
            <p className="text-sm font-bold text-blue-800 mb-1">Creative Dimensions Reference Guide</p>
            <p className="text-xs text-blue-600">Use these dimensions when creating images for IMPORTREND. Upload separate desktop and mobile versions for best results.</p>
          </div>
          {[
            { zone: "Hero Banner (Homepage Slider)", desktop: "1440 × 500 px", mobile: "768 × 400 px", ratio_d: "2.88:1", ratio_m: "1.92:1", note: "Set image_url (desktop) and image_url_mobile (mobile) in each hero slide", bg: "bg-indigo-50 border-indigo-200", accent: "bg-indigo-500" },
            { zone: "Category Grid — Large Card (1st)", desktop: "640 × 640 px", mobile: "Full Width", ratio_d: "1:1 square", ratio_m: "~2:1 landscape", note: "First category item spans 2 rows on desktop. Square image recommended.", bg: "bg-orange-50 border-orange-200", accent: "bg-orange-500" },
            { zone: "Category Grid — Small Cards (2nd–5th)", desktop: "320 × 320 px", mobile: "Same", ratio_d: "1:1 square", ratio_m: "1:1 square", note: "4 small cards in 2×2 grid alongside the large card", bg: "bg-orange-50 border-orange-200", accent: "bg-orange-400" },
            { zone: "Promo / Deal Banner", desktop: "720 × 300 px", mobile: "768 × 300 px", ratio_d: "2.4:1", ratio_m: "2.56:1", note: "Two banners side-by-side on desktop, stacked on mobile", bg: "bg-green-50 border-green-200", accent: "bg-green-500" },
            { zone: "Brand Logo", desktop: "200 × 80 px", mobile: "Same", ratio_d: "2.5:1", ratio_m: "2.5:1", note: "Transparent PNG works best on any background", bg: "bg-slate-50 border-slate-200", accent: "bg-slate-500" },
            { zone: "Category Page Hero Banner", desktop: "1440 × 300 px", mobile: "768 × 250 px", ratio_d: "4.8:1", ratio_m: "3.07:1", note: "Configured in Category Banners admin. Add both desktop & mobile URLs", bg: "bg-teal-50 border-teal-200", accent: "bg-teal-500" },
            { zone: "Favicon / App Icon", desktop: "32 × 32 px (.ico)", mobile: "180 × 180 px (.png)", ratio_d: "1:1", ratio_m: "1:1", note: "Place favicon.ico in /public/ folder. Apple Touch Icon: 180×180px", bg: "bg-rose-50 border-rose-200", accent: "bg-rose-500" },
          ].map(row => (
            <div key={row.zone} className={`border rounded-xl overflow-hidden ${row.bg}`}>
              <div className="flex items-center gap-2 px-4 py-3">
                <div className={`w-2.5 h-2.5 rounded-full ${row.accent}`} />
                <p className="text-sm font-bold text-slate-800">{row.zone}</p>
              </div>
              <div className="px-4 pb-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div>
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Desktop</p>
                  <p className="text-sm font-black text-slate-800 font-mono">{row.desktop}</p>
                  <p className="text-[11px] text-slate-500">{row.ratio_d}</p>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Mobile</p>
                  <p className="text-sm font-black text-slate-800 font-mono">{row.mobile}</p>
                  <p className="text-[11px] text-slate-500">{row.ratio_m}</p>
                </div>
                <div className="sm:col-span-2">
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Notes</p>
                  <p className="text-xs text-slate-600">{row.note}</p>
                </div>
              </div>
            </div>
          ))}
          {/* Visual Layout Map */}
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <p className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-4">Visual Layout Map — Homepage</p>
            <div className="space-y-2">
              <div className="w-full bg-indigo-100 border-2 border-dashed border-indigo-300 rounded-lg flex items-center justify-center" style={{height:72}}>
                <div className="text-center"><p className="text-xs font-bold text-indigo-700">Hero Banner · 1440×500px (Desktop) · 768×400px (Mobile)</p></div>
              </div>
              <div className="flex gap-2" style={{height:88}}>
                <div className="w-1/3 bg-orange-100 border-2 border-dashed border-orange-300 rounded-lg flex items-center justify-center">
                  <div className="text-center"><p className="text-[10px] font-bold text-orange-700">Large Card</p><p className="text-[9px] text-orange-500">640×640px</p></div>
                </div>
                <div className="flex-1 grid grid-cols-2 gap-2">
                  {[1,2,3,4].map(n=><div key={n} className="bg-orange-50 border-2 border-dashed border-orange-200 rounded flex items-center justify-center"><p className="text-[9px] text-orange-400 font-bold">320×320</p></div>)}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2" style={{height:56}}>
                {[1,2].map(n=><div key={n} className="bg-green-100 border-2 border-dashed border-green-300 rounded-lg flex items-center justify-center"><p className="text-[10px] font-bold text-green-700">Promo {n} · 720×300px</p></div>)}
              </div>
              <div className="flex gap-2" style={{height:64}}>
                <div className="w-16 bg-purple-100 border-2 border-dashed border-purple-300 rounded flex items-center justify-center"><div className="text-center"><p className="text-[8px] font-bold text-purple-700">Side</p><p className="text-[7px] text-purple-500">300×600</p></div></div>
                {[1,2,3,4].map(n=><div key={n} className="flex-1 bg-slate-100 border-2 border-dashed border-slate-200 rounded flex items-center justify-center"><p className="text-[8px] text-slate-400">Product</p></div>)}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
